/**
 * @file EmploymentCategoryDropdown.js
 * @description A dropdown component for selecting an employment category.
 */

import React, { useState, useEffect } from "react";
import { fetchEmploymentCategories } from "../../../services/systemService";
import { Dropdown } from "../../helpers/Dropdown";
import LoadingWrapper from "../../ui/loaders/LoadingWrapper";
import Toast from "../../ui/toasts/Toast";
import { getText } from '../../../i18n';

/**
 * A dropdown component for selecting employment categories.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {boolean} [props.inlineLabel=true] - Determines whether the label is displayed inline.
 * @param {string} props.selectedCategory - The currently selected employment category.
 * @param {Function} props.onCategoryChange - The callback function to handle category change.
 * @param {string} [props.label="Employment Category"] - The label text for the dropdown.
 * @param {boolean} [props.isRequired=false] - Determines whether the dropdown is required.
 * @param {boolean} [props.hideErrorMessage=false] - Determines whether to hide the error message text.
 */
const EmploymentCategoryDropdown = ({
  className,
  inlineLabel = true,
  selectedCategory,
  onCategoryChange,
  label = "Employment Category",
  isRequired = false,
  errorMessage,
  hideErrorMessage = false,
}) => {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const getCategories = async () => {
      setIsLoading(true);
      try {
        const data = await fetchEmploymentCategories();
        setCategories(data);
      } catch (error) {
        console.error("Could not fetch employment categories:", error);
        Toast.error(getText('msg_9b59fbd52185'));
      } finally {
        setIsLoading(false);
      }
    };

    getCategories();
  }, []);

  const getDefaultLabel = () => {
    const matchingCategory = categories.find(
      (category) => category.id === selectedCategory
    );
    return matchingCategory
      ? matchingCategory.name
      : "Select Employment Category";
  };

  const categoryOptions = categories.map((category) => ({
    value: category.id,
    label: category.name,
  }));

  return (
    <div
      className={`form-group mb-3 ${
        inlineLabel ? "inline-input" : ""
      } ${className} ${errorMessage ? "has-error" : ""}`}
    >
      <label htmlFor="employment-category-dropdown">
        {label}
        {isRequired && <span className="text-danger"> *</span>}
      </label>
      <LoadingWrapper isLoading={isLoading}>
        <Dropdown
          id="employment-category-dropdown"
          defaultValue={getDefaultLabel()}
          options={categoryOptions}
          value={selectedCategory}
          onChange={onCategoryChange}
          disabled={categories.length === 0 || isLoading}
        />
      </LoadingWrapper>
      {errorMessage && !hideErrorMessage && (
        <div className="invalid-feedback d-block">{errorMessage}</div>
      )}
    </div>
  );
};

export default EmploymentCategoryDropdown;
